<template>
	<div
		class="banner"
		data-cy="banner"
	>
		<helio-link
			:link="link"
			:target="linkTarget"
			class="banner__link"
			data-cy="banner-link"
			@click="click"
		>
			<div
				v-if="active && (background || title)"
				ref="banner"
				class="banner__main"
				data-cy="banner-main"
				:class="`banner__main--${size}`"
				:style="style"
			>
				<div
					v-if="title || subtitle"
					class="banner__text"
				>
					<div
						v-if="title"
						class="banner__title"
					>
						{{ title }}
					</div>

					<div
						v-if="subtitle"
						class="banner__subtitle"
					>
						{{ subtitle }}
					</div>
				</div>

				<div
					v-if="closeable"
					class="banner__close"
					data-cy="banner-close"
					tabindex="0"
					:aria-label="ariaLabel"
					@click="close"
					@keydown.space="close"
					@keydown.enter="close"
				>
					<helio-icon
						token="times"
						weight="light"
					/>
				</div>
			</div>
		</helio-link>
	</div>
</template>

<script>
import Icon from '../icon/Icon.vue';
import Link from '../link/Link.vue';

export default {
	name: 'Banner',

	components: {
		'helio-icon': Icon,
		'helio-link': Link,
	},

	props: {
		active: {
			type: Boolean,
			required: true,
			description: 'Sets whether or not the Banner will be shown',
		},

		background: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the background-image for the Banner',
		},

		closeable: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether a close icon (<i class="fal fa-times"></i>) will appear, permitting the user to close the Banner',
		},

		link: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the link on the title and subtitle of the Banner',
		},

		linkTarget: {
			type: String,
			required: false,
			default: '_self',
			validator: value => [
				'_blank',
				'_parent',
				'_self',
				'_top',
				'framename',
			].indexOf(value) !== -1,
			description: 'Sets the location where the linked document will be opened',
		},

		subtitle: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the subtitle of the Banner',
		},

		title: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the title of the Banner',
		},
	},

	data() {
		return {
			size: 'medium',
			style: null,
		};
	},

	computed: {
		ariaLabel() {
			let ariaLabel = this.$t('aria-label.close-button');

			if (this.title) {
				ariaLabel += `: ${this.title}`;
			}

			return ariaLabel;
		},
	},

	mounted() {
		this.onResize();
		window.addEventListener('resize', this.onResize);
	},

	beforeDestroy() {
		window.removeEventListener('resize', this.onResize);
	},

	methods: {
		onResize() {
			this.$nextTick(() => {
				const { banner } = this.$refs;

				if (banner) {
					if (banner.clientWidth <= 533) {
						this.size = 'small';
					} else if (banner.clientWidth <= 719) {
						this.size = 'medium';
					} else {
						this.size = 'large';
					}

					this.style = this.size === 'small' && this.title
						? 'background-image: none;'
						: `background-image: url(${this.background});`;
				}
			});
		},

		click() {
			if (!this.link) {
				this.$emit('click');
			}
		},

		close() {
			this.$emit('close');
		},
	},
};
</script>

<style lang="scss" src="./banner.scss" />
